import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/roles.actions';
import { rolesSelector } from 'redux/selectors/roles.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import RolesView from 'views/RolesView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IRole } from 'types/roles.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/RolesView/i18n';

const RolesContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IRole | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.roles);
  const items = useTypedSelector(rolesSelector);

  const actions = [
    {
      label: i18next.t<string>('ROLES_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/users/roles/${id}/edit`),
    },
    {
      label: i18next.t<string>('ROLES_VIEW:DELETE'),
      onClick: (id: string) => setDeleteItem(items.find((item: IRole) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'name',
      label: i18next.t<string>('ROLES_VIEW:NAME'),
    },
    {
      name: 'guardName',
      label: i18next.t<string>('ROLES_VIEW:GUARD_NAME'),
    },
    {
      name: 'id',
      label: i18next.t<string>('ROLES_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('ROLES_VIEW:TITLE')}>
      <RolesView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/users/roles/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('ROLES_VIEW:DELETE_DIALOG_MESSAGE', {
          name: `${deleteItem?.name} (${deleteItem?.guardName})`,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('ROLES_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default RolesContainer;
